import React, { useCallback, useEffect, useState } from 'react';
import Text from 'antd/lib/typography/Text';
import '@ant-design/compatible/assets/index.css';
import {
  message,
  Form,
  Col,
  Row,
  Radio,
  FormInstance,
  Select,
  InputNumber,
  Spin,
  CheckboxOptionType,
  Input,
} from 'antd';
import DF from 'DisplayFramework';
import AssetsTable from './AssetsTableEarlyTerm';
import * as finEngine from 'services/finEngine';
import FormInput from 'components/FormInput';
import { useQuery } from 'react-query';
import {
  getFinContractById,
  getFinModificationByModid,
  getFinModificationValidByCtrid,
  IrvScheduleResponse,
} from 'services/fincontract';
import { toCurrency, toRate } from 'utils/utils';
import ParListSelect from 'components/ParListSelect';
import { shouldUpdateCompare } from 'utils/utils2';
import PricingListFilteredSelect from 'components/PricingListFilteredSelect';
import { PricingResponse } from 'services/pricing';
import _, { debounce, isNil, sumBy } from 'lodash';
import TextArea from 'antd/lib/input/TextArea';
import UtinaCard from 'components/UtinaCard';
import { getProductParvalue } from 'services/parametersService';
import InputCurrency from 'components/InputCurrency/InputCurrency';
import ParListSelectWithLabelInValue from 'components/ParListSelectWithLabelInValue/ParListSelectWithLabelInValue';
import moment from 'moment';
import { AssetResponse } from 'services/proposal';

export type TabContractEarlyTerminationProps = {
  objectid: number;
  object: string;
  form?: FormInstance;
  action?: string; // EARLY or WAIVE
  setSuccessful?: any;
};

const TabContractEarlyTermination = ({
  objectid,
  object,
  form,
  action,
  setSuccessful,
}: TabContractEarlyTerminationProps) => {
  const [state, setState] = useState<any>({
    totalirvamount: 0,
    totalvatrefund: 0,
    totalfinrvamount: 0,
    totalsaleamount: 0,
    totalinvoiceamount: 0,
    currentRental: 0,
    currentOutstanding: 0,
    currentRV: 0,
    newOutstanding: 0,
    newRental: 0,
    newRV: 0,
    totaldegfeesamount: 0,
    totaltpofamount: 0,
    totalspamount: 0,
    taxcode: 'NORM',
    taxrate: 20,
    currcode: localStorage.getItem('currcode') ?? '',
    listAssets: [],
    feesamount: 0,
  });

  const [flagapplytoall, setFlagapplytoall] = useState(true);
  const [mode, setMode] = useState('SIMUL');
  const [Loading, setLoading] = useState(false);

  const updateState = useCallback(
    (values) => {
      setState((prev: any) => ({ ...prev, ...values }));
    },
    [setState]
  );

  const {
    data: contract,
    refetch,
    isLoading: contactLoading,
  } = useQuery(
    ['getFinContractById', { ctrid: objectid }],
    async () => {
      setLoading(true);
      try {
        const res = await getFinContractById({ ctrid: objectid });
        setLoading(false);
        if (state.listAssets?.length === 0)
          updateState({
            listAssets: res?.data?.assets.filter(
              (a) => a.assetstatus === 'ACTIVE'
            ),
          });
        return res.data;
      } catch (err) {
        setLoading(false);
      }
    },
    { staleTime: Infinity }
  );

  useEffect(() => {
    if (contract) {
      if (state.listAssets?.length === 1) {
        handleChangeApplyToAll(true);
      } else {
        handleChangeApplyToAll(false);
      }
    }
  }, [contract, contactLoading]);

  useEffect(() => {
    updateState({
      listAssets: [],
    });
    refetch();
  }, []);

  const { data: vatrefundduration } = useQuery(
    ['getProductParvalue', { ctrid: objectid }],
    () =>
      getProductParvalue({
        mgid: Number(localStorage.getItem('mgid')),
        parcode: 'VATREFUND',
        prcode: contract?.prcode ?? 'LEASING',
      }).then((res) => {
        return res.data;
      }),
    { enabled: contract !== null, refetchOnMount: true }
  );

  const { data: modList, isFetching } = useQuery(
    ['getFinModificationValidByCtrid', { ctrid: objectid }],
    () =>
      getFinModificationValidByCtrid({
        ctrid: objectid,
        modevent: 'EARLYTERM',
      }).then((res) => res.list),
    { refetchOnMount: true }
  );

  useEffect(() => {
    form?.setFieldsValue({
      event: 'tabCTEarlyTerm',
    });
    setSuccessful(contactLoading && isFetching);
  }, [contactLoading, isFetching]);

  const optionsApplyToAll: CheckboxOptionType[] = [
    {
      label: 'Yes',
      value: true,
      style: flagapplytoall ? { background: 'black', color: 'white' } : {},
    },
    {
      label: 'No',
      value: false,
      style: !flagapplytoall ? { background: 'black', color: 'white' } : {},
    },
  ];

  let cteaprrate =
    contract?.conElements?.find((e) => e.ctetype === 'FIN')?.cteaprrate ?? 0;

  let finConElem: any[] =
    contract?.conElements?.find((e) => e.ctetype === 'FIN')?.conElemSchedules ??
    [];

  const notInvoicedSchedule = finConElem.filter((s) => s.ciid === null);

  let minCtsduedate = _.minBy(finConElem, 'ctsduedate')?.ctsduedate ?? '';

  const optionDates = notInvoicedSchedule?.map(
    (e, index) =>
      e.ctstype !== 'UPFRONT' &&
      e.ctsduedate !== minCtsduedate &&
      e.ctstype !== 'DEFINT' && (
        <option key={index} value={e.ctsduedate.toString()}>
          {e.ctsduedate}
        </option>
      )
  );

  const optionModListValid = modList?.map((e, index) => (
    <Select.Option key={index} value={e.modid}>
      {e.modcomment}
    </Select.Option>
  ));

  const optionsMode = [
    {
      label: 'Simulation',
      value: 'SIMUL',
      style: mode === 'SIMUL' ? { background: 'black', color: 'white' } : {},
    },
    {
      label: 'Real',
      value: 'REAL',
      style: mode === 'REAL' ? { background: 'black', color: 'white' } : {},
    },
  ];

  const handleFinModSelect = (finmod: number) => {
    if (finmod === undefined) {
      updateState({ isModSelected: false });
      return;
    }
    updateState({ isLoading: true });

    let query = getFinModificationByModid({ modid: finmod });

    query.then((response) => {
      if (response.code !== '200') {
        return;
      }

      const res = response.data;

      let degfeesformulaIN = res.simulParams.filter(
        (item) => item.parcode === 'DEGFF' && item.partype === 'IN'
      )[0];
      let degfeesformula = {
        priid: Number(degfeesformulaIN.newvalue),
        prilabel: degfeesformulaIN.parcomment,
      };

      let spformulaIN = res.simulParams.filter(
        (item) => item.parcode === 'SPFORMULA' && item.partype === 'IN'
      )[0];
      let spformula = {
        priid: Number(spformulaIN.newvalue),
        prilabel: spformulaIN.parcomment,
      };

      let tpofformulaIN = res.simulParams.filter(
        (item) => item.parcode === 'TPFORMULA' && item.partype === 'IN'
      )[0];
      let tpofspformula = {
        priid: Number(tpofformulaIN.newvalue),
        prilabel: tpofformulaIN.parcomment,
      };

      setMode('REAL');

      updateState({
        mode: 'REAL',
        listAssets: res.simulParams.filter(
          (item) => item.parcode === 'ASSETS' && item.partype === 'IN'
        )[0]?.finModSimulParamAssets,
        effectDate: res.simulParams.filter(
          (item) => item.parcode === 'EFFECTDATE' && item.partype === 'IN'
        )[0].newvalue,
        flagapplytoall: res.simulParams.filter(
          (item) => item.parcode === 'FLAGATA' && item.partype === 'IN'
        )[0]?.newvalue,
        targetphase: res.simulParams.filter(
          (item) => item.parcode === 'TARGETPH' && item.partype === 'IN'
        )[0]?.newvalue,
        targetstatus: res.simulParams.filter(
          (item) => item.parcode === 'TARGETST' && item.partype === 'IN'
        )[0]?.newvalue,
        damage: res.simulParams.filter(
          (item) => item.parcode === 'DAMAGE' && item.partype === 'IN'
        )[0]?.newvalue,
        degfeesformula: degfeesformula,
        degfeesamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTGEGF' && item.partype === 'IN'
        )[0]?.newvalue,
        spvalue: res.simulParams.filter(
          (item) => item.parcode === 'SPVALUE' && item.partype === 'IN'
        )[0]?.newvalue,
        spformula: spformula,
        spamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTSP' && item.partype === 'IN'
        )[0]?.newvalue,
        totalfinrvamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTFINRV' && item.partype === 'IN'
        )[0]?.newvalue,
        totalirvamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTIRV' && item.partype === 'IN'
        )[0]?.newvalue,
        totalsaleamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTSALE' && item.partype === 'IN'
        )[0]?.newvalue,
        tpofformula: tpofspformula,
        tpofamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTALPOF' && item.partype === 'IN'
        )[0]?.newvalue,
        totalinvoiceamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTINV' && item.partype === 'IN'
        )[0]?.newvalue,
        totaltpofamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTALPOF' && item.partype === 'IN'
        )[0]?.newvalue,
        totaldegfeesamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTGEGF' && item.partype === 'IN'
        )[0]?.newvalue,
        totalspamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTSP' && item.partype === 'IN'
        )[0]?.newvalue,
        currentRental: res.simulParams.filter(
          (item) => item.parcode === 'NEWRENTAL' && item.partype === 'IN'
        )[0]?.oldvalue,
        newRental: res.simulParams.filter(
          (item) => item.parcode === 'NEWRENTAL' && item.partype === 'IN'
        )[0]?.newvalue,
        currentOutstanding: res.simulParams.filter(
          (item) => item.parcode === 'NEWOUT' && item.partype === 'IN'
        )[0]?.oldvalue,
        newOutstanding: res.simulParams.filter(
          (item) => item.parcode === 'NEWOUT' && item.partype === 'IN'
        )[0]?.newvalue,
        currentRV: res.simulParams.filter(
          (item) => item.parcode === 'NEWRV' && item.partype === 'IN'
        )[0]?.oldvalue,
        newRV: res.simulParams.filter(
          (item) => item.parcode === 'NEWRV' && item.partype === 'IN'
        )[0]?.newvalue,
        comment: res.modcomment,

        reason: res.simulParams.find(
          (item) => item.parcode === 'ENDREASON' && item.partype === 'IN'
        )?.parcomment,
        fees: res.simulParams.find(
          (item) => item.parcode === 'FEESTYPE' && item.partype === 'IN'
        )?.newvalue,
        feesamount: Number(
          res.simulParams.find(
            (item) => item.parcode === 'FEESAMOUNT' && item.partype === 'IN'
          )?.newvalue
        ),
        feeslabel: res.simulParams.find(
          (item) => item.parcode === 'FEESTYPE' && item.partype === 'IN'
        )?.parcomment,
      });

      let test = res.simulParams.filter(
        (item) => item.parcode === 'ASSETS' && item.partype === 'IN'
      )[0]?.finModSimulParamAssets;

      setFlagapplytoall(
        res.simulParams.filter(
          (item) => item.parcode === 'FLAGATA' && item.partype === 'IN'
        )[0]?.newvalue === 'true' || false
      );

      if (!flagapplytoall) {
        let totalfeesAsset = 0;

        let listAssets = res.simulParams.filter(
          (item) => item.parcode === 'ASSETS' && item.partype === 'IN'
        )[0]?.finModSimulParamAssets;

        listAssets.forEach((asset: any) => {
          totalfeesAsset += asset.feesamount;
        });

        updateState({ feesamount: totalfeesAsset });
      }

      form?.setFieldsValue({
        mode: 'REAL',

        effectDate: res.simulParams.filter(
          (item) => item.parcode === 'EFFECTDATE' && item.partype === 'IN'
        )[0].newvalue,
        flagapplytoall: res.simulParams.filter(
          (item) => item.parcode === 'FLAGATA' && item.partype === 'IN'
        )[0]?.newvalue,
        targetphase: res.simulParams.filter(
          (item) => item.parcode === 'TARGETPH' && item.partype === 'IN'
        )[0]?.newvalue,
        targetstatus: res.simulParams.filter(
          (item) => item.parcode === 'TARGETST' && item.partype === 'IN'
        )[0]?.newvalue,
        damage: res.simulParams.filter(
          (item) => item.parcode === 'DAMAGE' && item.partype === 'IN'
        )[0]?.newvalue,
        degfeesformula: degfeesformula,
        degfeesamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTGEGF' && item.partype === 'IN'
        )[0]?.newvalue,
        spvalue: res.simulParams.filter(
          (item) => item.parcode === 'SPVALUE' && item.partype === 'IN'
        )[0]?.newvalue,
        spformula: spformula,
        spamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTSP' && item.partype === 'IN'
        )[0]?.newvalue,
        totalfinrvamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTFINRV' && item.partype === 'IN'
        )[0]?.newvalue,
        totalirvamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTIRV' && item.partype === 'IN'
        )[0]?.newvalue,
        totalsaleamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTSALE' && item.partype === 'IN'
        )[0]?.newvalue,
        tpofformula: tpofspformula,
        tpofamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTALPOF' && item.partype === 'IN'
        )[0]?.newvalue,
        totalinvoiceamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTINV' && item.partype === 'IN'
        )[0]?.newvalue,
        totaltpofamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTALPOF' && item.partype === 'IN'
        )[0]?.newvalue,
        totaldegfeesamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTGEGF' && item.partype === 'IN'
        )[0]?.newvalue,
        totalspamount: res.simulParams.filter(
          (item) => item.parcode === 'TOTSP' && item.partype === 'IN'
        )[0]?.newvalue,
        currentRental: res.simulParams.filter(
          (item) => item.parcode === 'NEWRENTAL' && item.partype === 'IN'
        )[0]?.oldvalue,
        newRental: res.simulParams.filter(
          (item) => item.parcode === 'NEWRENTAL' && item.partype === 'IN'
        )[0]?.newvalue,
        currentOutstanding: res.simulParams.filter(
          (item) => item.parcode === 'NEWOUT' && item.partype === 'IN'
        )[0]?.oldvalue,
        newOutstanding: res.simulParams.filter(
          (item) => item.parcode === 'NEWOUT' && item.partype === 'IN'
        )[0]?.newvalue,
        currentRV: res.simulParams.filter(
          (item) => item.parcode === 'NEWRV' && item.partype === 'IN'
        )[0]?.oldvalue,
        newRV: res.simulParams.filter(
          (item) => item.parcode === 'NEWRV' && item.partype === 'IN'
        )[0]?.newvalue,
        comment: res.modcomment,
        listAssets: res.simulParams.filter(
          (item) => item.parcode === 'ASSETS' && item.partype === 'IN'
        )[0]?.finModSimulParamAssets,
        reason: res.simulParams.find(
          (item) => item.parcode === 'ENDREASON' && item.partype === 'IN'
        )?.parcomment,
        fees: res.simulParams.find(
          (item) => item.parcode === 'FEESTYPE' && item.partype === 'IN'
        )?.newvalue,
        feesamount: Number(
          res.simulParams.find(
            (item) => item.parcode === 'FEESAMOUNT' && item.partype === 'IN'
          )?.newvalue
        ),
        feeslabel: res.simulParams.find(
          (item) => item.parcode === 'FEESTYPE' && item.partype === 'IN'
        )?.parcomment,
      });

      let selectedAssets =
        state?.listAssets?.map((asset: any) => asset.assetid) ?? [];
      setselectedassets([...selectedAssets]);

      updateState({ isModSelected: true });

      updateState({ isLoading: false });
    });
  };

  const handleEffectChange = (effectDate: string) => {
    // //

    // resetToManual();

    // eslint-disable-next-line no-unused-vars
    let totalirvamount = 0;
    // eslint-disable-next-line no-unused-vars
    let totalfinrvamount = 0;
    let updatelist = state.listAssets;
    let currentRental = 0;
    let currentrvamount = 0;
    let currentoutstanding = 0;
    let totalNiemeTva = 0;

    updatelist.forEach((asset: AssetResponse & any) => {
      let irvlineEffectDate: IrvScheduleResponse = asset?.irvSchedule?.find(
        (i: any) => i.enddate === effectDate
      )!;

      let elem = contract?.conElements.find(
        (el) => el.ctetype === 'FIN' && el.cteid === asset.cteid
      );

      let startdate = moment(elem?.ctestartdate);

      let duree = moment(effectDate).diff(startdate, 'days');

      console.log('elem', elem, elem?.ctemileage);

      let mileagePerDay = elem?.ctemileage ? elem?.ctemileage / 365 : 0;

      let maxMileageToUse = mileagePerDay * duree;

      console.log('maxMileageToUse', maxMileageToUse);

      asset.maxMileageToUse = maxMileageToUse?.toFixed(2);

      if (updatelist.length > 0) {
        form?.setFieldValue('contractualmillage', maxMileageToUse?.toFixed(2));
      }

      let order = irvlineEffectDate?.irvorder;

      if (order > 1) {
        order = order - 1;
      }

      let niemetvaamount = 0;

      if (
        selectedassets.findIndex((assetid) => assetid === asset.assetid) !== -1
      ) {
        if (asset.assetsegment === 'MOBILIER') {
          let assetDelivY = moment(asset.assetdeliverydate).format('Y');
          let effectDateY = moment(effectDate).format('Y');

          let nbYears = Math.abs(Number(effectDateY) - Number(assetDelivY)) + 1;

          if (nbYears > 5) {
            nbYears = 5;
          }
          let remainingYearsTva = 5 - nbYears;

          niemetvaamount = (asset.assettaxamount / 5) * remainingYearsTva;

          totalNiemeTva += niemetvaamount;
        } else if (asset.assetsegment === 'IMMO') {
          let assetDelivY = moment(asset.assetdeliverydate).format('Y');
          let effectDateY = moment(effectDate).format('Y');

          let nbYears = Math.abs(Number(effectDateY) - Number(assetDelivY)) + 1;

          if (nbYears > 10) {
            nbYears = 10;
          }
          let remainingYearsTva = 5 - nbYears;

          niemetvaamount = (asset.assettaxamount / 10) * remainingYearsTva;

          totalNiemeTva += niemetvaamount;
        }
      }

      let irvline: IrvScheduleResponse = asset?.irvSchedule?.find(
        (i: any) => i.irvorder === order
      )!;

      currentrvamount += asset.assetrvamount;
      currentoutstanding += irvline?.finrvamount;
      totalirvamount += irvline?.irvamount;
      totalfinrvamount += irvline?.finrvamount;

      asset.rvgap = irvline?.rvgap;
      asset.irvamount = irvline?.irvamount;
      asset.finrvamount = irvline?.finrvamount;
      asset.rentalpart = irvline?.rentalpart;
      asset.capitalpart = irvline?.capitalpart;
      asset.saleamount = 0;
      asset.totalinvoiceamount = 0;
      asset.spamount = 0;
      asset.degfeesamount = 0;
      asset.tpofamount = 0;
      asset.vatrefund = 0;
      asset.targetphase = '';
      asset.targetstatus = '';
      currentRental += irvline?.rentalpart;
    });

    form?.setFieldsValue({
      currentRental: currentRental,
      currentOutstanding: currentoutstanding,
      newOutstanding: currentoutstanding,
      currentRV: currentrvamount,
      newRV: currentrvamount,
      niemetvaamount: totalNiemeTva,
      totalvatrefund: 0,
      degfeesformula: null,
      tpofformula: null,
      targetstatus: '',
      targetphase: '',
      damage: '',
      degfeesamount: '',
      spvalue: '',
      spformula: '',
      spamount: '',
      totalfinrvamount: 0,
      totalirvamount: 0,
      totalsaleamount: 0,
      tpofamount: 0,
      totalinvoiceamount: 0,
      totaltpofamount: 0,
      totaldegfeesamount: 0,
      totalspamount: 0,
      newRental: currentRental,
      comment: '',
      reason: '',
      fees: '',
      feesamount: 0,
      feeslabel: '',
    });

    updateState({
      listAssets: updatelist,
      totalirvamount: 0,
      totalfinrvamount: 0,
      totalvatrefund: 0,
      totalsaleamount: 0,
      totaldegfeesamount: 0,
      totalspamount: 0,
      totaltpofamount: 0,
      totalinvoiceamount: 0,
      degfeesformula: null,
      tpofformula: null,
      currentRental: currentRental,
      currentOutstanding: currentoutstanding,
      currentRV: currentrvamount,
      newOutstanding: currentoutstanding,
      newRental: currentRental,
      newRV: currentrvamount,
      targetstatus: '',
      targetphase: '',

      /*sumBy(
        state.listAssets.filter((asset: any) =>
          _.includes([...selectedassets], asset.assetid)
        ),
        'degfeesamount'
      ),*/
    });

    // handleChangeApplyToAll(true);
  };

  const calculateFees = (pricing?: PricingResponse, ctetype?: string) => {
    if (pricing) {
      // if (pricing.calculationmethod==='OUTINT' || 'OUTRENTAL' || 'TOTXINT') {

      //   // add here the call of the new API : getTPofFees

      //   const feesamount = state.feesamount ?? 0;

      //   if (ctetype === 'TPOF') {
      //     let totalinvoiceamount =
      //       Number(state.totalsaleamount) +
      //       Number(state.totaldegfeesamount) +
      //       Number(res.installmentamount) +
      //       Number(state.totalspamount) +
      //       feesamount;

      //     let totaltpofamount = res.installmentamount;

      //     let listAssets = state.listAssets.map((asset: any) => {
      //       let newtotal = 0;
      //       if (form?.getFieldValue('targetphase') === 'ASSETOUT') {
      //         newtotal = asset.saleamount + res.installmentamount / coef;
      //       } else {
      //         newtotal =
      //           asset.degfeesamount +
      //           asset.spamount +
      //           res.installmentamount / coef;
      //       }

      //       return {
      //         ...asset,
      //         tpofamount: res.installmentamount / coef,
      //         totalinvoiceamount: newtotal,
      //       };
      //     });
      //     updateState({
      //       totalinvoiceamount: totalinvoiceamount,
      //       totaltpofamount: totaltpofamount,
      //       listAssets: listAssets,
      //     });

      //     form?.setFieldsValue({
      //       totalinvoiceamount: totalinvoiceamount,
      //       totaltpofamount: totaltpofamount,
      //       listAssets: listAssets,
      //     });

      // }else {

      let totalamount = sumBy(state.listAssets, 'assetnetamount');

      finEngine
        .calculateServiceWithStep({
          servReq: {
            finSchedule: [],
            inDownPayment: 0,
            inFinAmount: state.currentOutstanding ?? 0,
            inFollowGP: true,
            inGPDuration: 0,
            inPstype: pricing?.ctecategory ?? '',
            inRoudingAmount: 2,
            inServcalculationmethod:
              pricing?.serviceResponse.servcalculationmethod ?? '',
            inServfixedamount: pricing?.serviceResponse.servfixedamount ?? 0,
            inServiceduration: pricing?.serviceResponse.servduration ?? '',
            inServpercentage: pricing?.serviceResponse.servpercentage ?? 0,
            inTaxcode: pricing?.taxcode ?? '',
            inTaxrate: pricing?.taxrate ?? 0,
            inDuration: 0,
            inInvestAmount: totalamount,
            inAssetAmount: totalamount,
            inServperiodicity: pricing?.serviceResponse.servperiodicity ?? '1',
            inServDurationFree:
              pricing?.serviceResponse.servdurationdefault ?? 1,
            inStartDate: form?.getFieldValue('effectDate'),
            inServbasis: pricing?.serviceResponse.servbasis ?? 'FLAT',

            inCalculatedamountbasis:
              pricing?.serviceResponse.calculatedamountbasis ?? 'FINANCING',

            inMarginoncalcultedamount:
              pricing?.serviceResponse.marginoncalulatedamount ?? 0,
            inAdditionalcostaftermargin:
              pricing?.serviceResponse.additionalcostaftermargin ?? 0,

            servStep: pricing?.serviceStepList ?? [],
          },
        })
        .then((res) => {
          if (res.success) {
            message.success('Service - ' + res.message);

            let coef = state.listAssets?.length;

            // //

            const feesamount = state.feesamount ?? 0;

            if (ctetype === 'TPOF') {
              let totalinvoiceamount =
                Number(state.totalsaleamount) +
                Number(state.totaldegfeesamount) +
                Number(res.installmentamount) +
                Number(state.totalspamount) +
                feesamount;

              let totaltpofamount = res.installmentamount;

              let listAssets = state.listAssets.map((asset: any) => {
                let newtotal = 0;
                if (form?.getFieldValue('targetphase') === 'ASSETOUT') {
                  newtotal = asset.saleamount + res.installmentamount / coef;
                } else {
                  newtotal =
                    asset.degfeesamount +
                    asset.spamount +
                    res.installmentamount / coef;
                }

                return {
                  ...asset,
                  tpofamount: res.installmentamount / coef,
                  totalinvoiceamount: newtotal,
                };
              });
              updateState({
                totalinvoiceamount: totalinvoiceamount,
                totaltpofamount: totaltpofamount,
                listAssets: listAssets,
              });

              form?.setFieldsValue({
                totalinvoiceamount: totalinvoiceamount,
                totaltpofamount: totaltpofamount,
                listAssets: listAssets,
              });
            } else {
              if (ctetype === 'DEGFEES') {
                let totalinvoiceamount =
                  Number(state.totalsaleamount) +
                  Number(res.installmentamount) +
                  Number(state.totaltpofamount) +
                  Number(state.totalspamount) +
                  feesamount;

                let totaldegfeesamount = res.installmentamount;

                let listAssets = state.listAssets.map((asset: any) => {
                  const newtotal =
                    !isNaN(asset.saleamount) &&
                    form?.getFieldValue('targetphase') === 'ASSETOUT'
                      ? asset.saleamount + asset.tpofamount
                      : asset.tpofamount +
                        asset.spamount +
                        res.installmentamount / coef;
                  return {
                    ...asset,
                    totalinvoiceamount: newtotal,
                    degfeesamount: res.installmentamount / coef,
                  };
                });
                updateState({
                  totalinvoiceamount: totalinvoiceamount,
                  totaldegfeesamount: totaldegfeesamount,
                  listAssets: listAssets,
                });
                form?.setFieldsValue({
                  totalinvoiceamount: totalinvoiceamount,
                  totaldegfeesamount: totaldegfeesamount,
                  listAssets: listAssets,
                });
              } else {
                // SPFEES :
                let spamount =
                  res.installmentamount * form?.getFieldValue('spvalue');
                let totalinvoiceamount =
                  Number(state.totalsaleamount) +
                  Number(state.totaldegfeesamount) +
                  Number(state.totaltpofamount) +
                  +feesamount +
                  spamount;

                totalinvoiceamount +=
                  Number(form?.getFieldValue('feesamout')) ?? 0;

                let listAssets = state.listAssets.map((asset: any) => {
                  const newtotal =
                    !isNaN(asset.saleamount) &&
                    form?.getFieldValue('targetphase') === 'ASSETOUT'
                      ? asset.saleamount + asset.tpofamount
                      : asset.tpofamount +
                        asset.degfeesamount +
                        spamount / coef;
                  return {
                    ...asset,
                    totalinvoiceamount: newtotal,
                    spamount: spamount / coef,
                  };
                });

                updateState({
                  totalinvoiceamount: totalinvoiceamount,
                  totalspamount: spamount,
                  listAssets: listAssets,
                });
                form?.setFieldsValue({
                  totalinvoiceamount: totalinvoiceamount,
                  totalspamount: spamount,
                  listAssets: listAssets,
                });
              }
            }
          }
        });
    }
    // } // end if pricing
  };

  // const calculateDepassedMileage = (ctemileage: number) => {
  //   let list = state.listAssets;

  //   list.foreach((asset:any)=>({
  //     let element = form
  //   }))
  // };

  const [selectedassets, setselectedassets] = useState<Array<number>>([]);

  const handletargetstatus = (targetstatus: any) => {
    let list = state.listAssets;

    updateState({
      listAssets: list.map((asset: any) => {
        return {
          ...asset,
          targetstatus: targetstatus,
        };
      }),
    });
  };

  const handleChangeApplyToAll = (val: boolean) => {
    // resetToManual();
    setFlagapplytoall(val);
    form?.setFieldsValue({
      flagapplytoall: val,
      targetstatus: '',
      targetphase: '',
    });

    if (val && state.listAssets) {
      //setselectedassets([...contract?.assets.map((asset) => asset.assetid)]);
      setselectedassets([
        ...(state?.listAssets?.map((asset: any) => asset.assetid) ?? []),
      ]);
    } else {
      setselectedassets([]);
    }
  };
  const handleTargetPhaseChange = (target: any) => {
    let list = state.listAssets;

    if (target === 'ASSETOUT') {
      let effectDate = form?.getFieldValue('effectDate');

      let nbyears =
        Number(vatrefundduration) -
        (Number(effectDate.substring(0, 4)) -
          Number(contract?.ctactivationdate.substring(0, 4)) +
          1);
      if (nbyears === null || nbyears === undefined) {
        nbyears = 0;
      }

      const saleamount =
        sumBy(list, 'finrvamount') > sumBy(list, 'irvamount')
          ? sumBy(list, 'finrvamount')
          : sumBy(list, 'irvamount');

      let listAssets = list.map((asset: any) => {
        return {
          ...asset,
          vatrefund: (asset.assettaxamount * nbyears) / 5,
          totalinvoiceamount:
            asset.finrvamount > asset.irvamount
              ? asset.finrvamount
              : asset.irvamount,
          saleamount:
            asset.finrvamount > asset.irvamount
              ? asset.finrvamount
              : asset.irvamount,
          spamount: 0,
          degfeesamount: 0,
          tpofamount: 0,
          targetphase: target,
        };
      });

      form?.setFieldsValue({
        totalirvamount: sumBy(list, 'irvamount'),
        totalfinrvamount: sumBy(list, 'finrvamount'),
        totalvatrefund: sumBy(list, 'vatrefund'),
        totalinvoiceamount: saleamount,
        totalsaleamount: saleamount,
        targetstatus: '',
        degfeesformula: null,
        spformula: null,
        spvalue: null,
        tpofformula: null,
        totaltpofamount: 0,
        totaldegfeesamount: 0,
        totalspamount: 0,
        fees: '',
        feeslabel: '',
        feesamount: 0,
        listAssets: listAssets,
        newOutstanding: state.currentOutstanding,
        newRental: state.currentRental,
        newRV: state.currentRV,
      });

      updateState({
        totalirvamount: sumBy(list, 'irvamount'),
        totalfinrvamount: sumBy(list, 'finrvamount'),
        totalvatrefund: sumBy(list, 'totalvatrefund'),
        totalsaleamount: saleamount,
        totalinvoiceamount: saleamount,
        totaldegfeesamount: 0,
        totaltpofamount: 0,
        totalspamount: 0,
        listAssets: listAssets,
        fees: '',
        feeslabel: '',
        feesamount: 0,
        newOutstanding: state.currentOutstanding,
        newRental: state.currentRental,
        newRV: state.currentRV,
      });
    } else {
      let listAssets = list.map((asset: any) => {
        return {
          ...asset,
          targetphase: target,
          saleamount: 0,
          spamount: 0,
          degfeesamount: 0,
          tpofamount: 0,
          totalinvoiceamount: 0,
          vatrefund: 0,
        };
      });
      updateState({
        totalirvamount: 0,
        totalfinrvamount: 0,
        totalsaleamount: 0,
        totalinvoiceamount: 0,
        newOutstanding: 0,
        newRental: 0,
        newRV: 0,
        totaldegfeesamount: 0,
        totaltpofamount: 0,
        totalspamount: 0,
        totalvatrefund: 0,
        listAssets: listAssets,
        fees: '',
        feeslabel: '',
        feesamount: 0,
      });

      form?.setFieldsValue({
        totalirvamount: sumBy(list, 'irvamount'),
        totalfinrvamount: sumBy(list, 'finrvamount'),
        targetstatus: '',
        degfeesformula: null,
        spformula: null,
        spvalue: null,
        tpofformula: null,
        totaltpofamount: 0,
        totaldegfeesamount: 0,
        totalspamount: 0,
        totalvatrefund: 0,
        listAssets: listAssets,
        fees: '',
        feeslabel: '',
        feesamount: 0,
        newOutstanding: 0,
        newRV: 0,
        newRental: 0,
      });
    }
  };
  const resetToManual = () => {
    setMode('SIMUL');
    form?.setFieldsValue({ mode: 'SIMUL' });

    if (contract && state.listAssets?.length > 1) {
      setFlagapplytoall(false);
      setselectedassets([]);
    }

    const listAssets = contract?.assets?.filter(
      (a) => a.assetstatus === 'ACTIVE'
    );
    updateState({
      listAssets: listAssets,
      totalirvamount: 0,
      totalfinrvamount: 0,
      newOutstanding: 0,
      newRental: 0,
      newRV: 0,
      totaldegfeesamount: 0,
      totaltpofamount: 0,
      totalspamount: 0,
      totalsaleamount: 0,
      totalinvoiceamount: 0,
      fees: '',
      feeslabel: '',
      feesamount: 0,
      totalvatrefund: 0,
    });
    form?.setFieldsValue({
      targetphase: null,
      targetstatus: null,
      damage: null,
      degfeesformula: null,
      spformula: null,
      tpofformula: null,
      modid: '' as any,
    });
  };
  const handleTarget = (target: any) => {
    let list = state.listAssets;
    let updatedList = list.map((asset: any) => {
      if (asset.assetid === target.assetid)
        return {
          ...asset,
          ...target,
          targetphase: target.targetphase,
          degfeesamount: target.degfeesamount,
          spamount: target.spamount,
          tpofamount: target.tpofamount,
          saleamount: target.saleamount,
          totalinvoiceamount: target.totalinvoiceamount,
          vatrefund: target.vatrefund,
          fees: target.fees,
          feesamount: target?.feesamount,
          feeslabel: target?.feeslabel,
        };

      return asset;
    });

    setselectedassets([...selectedassets, target.assetid]);

    const outstandingAmount = sumBy(
      updatedList.filter((e: any) => e.targetphase === ''),
      'finrvamount'
    );

    const totalOustandingAmount = sumBy(updatedList, 'finrvamount');

    const newRental =
      (state.currentRental * outstandingAmount) / totalOustandingAmount;

    const outstandingRVAmount = sumBy(
      updatedList.filter((e: any) => e.targetphase === ''),
      'assetrvamount'
    );

    let newlistAssets = updatedList.filter((asset: any) =>
      [...selectedassets, target.assetid].includes(asset.assetid)
    );

    let feesamount = 0;

    newlistAssets?.forEach((element: any) => {
      feesamount += element.feesamount;
    });

    form?.setFieldsValue({
      listAssets: newlistAssets,
      feesamount: feesamount,
      totaldegfeesamount: sumBy(updatedList, 'degfeesamount'),
      totalspamount: sumBy(updatedList, 'spamount'),
      totalvatrefund: sumBy(updatedList, 'vatrefund'),
      totaltpofamount: sumBy(updatedList, 'tpofamount'),
      totalsaleamount: sumBy(updatedList, 'saleamount'), //state.totalsaleamount + target.saleamount,
      totalinvoiceamount: sumBy(updatedList, 'totalinvoiceamount'),
      newRental: newRental,
      newOutstanding: outstandingAmount,
      newRV: outstandingRVAmount,
    });

    updateState({
      listAssets: updatedList,
      feesamount: feesamount,
      newOutstanding: outstandingAmount,
      newRental: newRental,
      newRV: outstandingRVAmount,
      totalvatrefund: sumBy(updatedList, 'vatrefund'),
      totaldegfeesamount: sumBy(updatedList, 'degfeesamount'), //state.totaldegfeesamount + target.degfeesamount,
      totaltpofamount: sumBy(updatedList, 'tpofamount'), //state.totaltpofamount + target.tpofamount,
      totalspamount: sumBy(updatedList, 'spamount'), //state.totalspamount + target.spamount,
      totalsaleamount: sumBy(updatedList, 'saleamount'), //state.totalsaleamount + target.saleamount,
      totalinvoiceamount: sumBy(updatedList, 'totalinvoiceamount'), //state.totalinvoiceamount + target.totalinvoiceamount,
    });
  };
  // const rowSelection = {
  //   /*onSelect: (selectedRowKeys: any, isSelected: any, selectedRows: any) => {

  //

  //     if (isSelected) {
  //      state.selectedAsset=selectedRows.assetid;
  //       open();
  //     }
  //   },*/
  //   onchange: (selectedRowKeys: any, selectedRows: any) => {
  //     //todo
  //   },
  // };
  const allrowSelection = {
    selectedRowKeys: [...selectedassets],
    onSelect: (selectedRow: any, isSelect: any) => {
      if (!flagapplytoall) {
        const newSelected = selectedassets.filter(
          (assetid) => assetid !== selectedRow.assetid
        );
        const selectedOutstandingAmount = state.listAssets.filter(
          (e: any) => e.assetid === selectedRow.assetid
        )[0].finrvamount;
        const newOutstanding =
          selectedOutstandingAmount +
          sumBy(
            state.listAssets.filter((e: any) => e.targetphase === ''),
            'finrvamount'
          );

        // const totalOutstandingAmount = sumBy(state.listAssets, 'finrvamount');
        // const coef = newOutstanding / totalOutstandingAmount;

        const selectedRentalAmount = state.listAssets.filter(
          (e: any) => e.assetid === selectedRow.assetid
        )[0].rentalpart;
        const newRental =
          selectedRentalAmount +
          sumBy(
            state.listAssets.filter((e: any) => e.targetphase === ''),
            'rentalpart'
          );

        const selectedRVAmount = state.listAssets.filter(
          (e: any) => e.assetid === selectedRow.assetid
        )[0].assetrvamount;
        const newRVAmount =
          selectedRVAmount +
          sumBy(
            state.listAssets.filter((e: any) => e.targetphase === ''),
            'assetrvamount'
          );
        form?.setFieldsValue({
          listAssets: state.listAssets
            .map((asset: any) => {
              if (asset.assetid === selectedRow.assetid)
                return {
                  ...asset,
                  targetphase: '',
                  degfeesamount: 0,
                  tpofamount: 0,
                  saleamount: 0,
                  spamount: 0,
                  vatrefund: 0,
                  totalinvoiceamount: 0,
                };
              return asset;
            })
            .filter((asset: any) => newSelected.includes(asset.assetid)),
          totaldegfeesamount: sumBy(
            state.listAssets.filter(
              (asset: any) =>
                _.includes([...newSelected], asset.assetid) &&
                !isNaN(asset.degfeesamount)
            ),
            'degfeesamount'
          ),

          feesamount: sumBy(
            state.listAssets.filter(
              (asset: any) =>
                _.includes([...newSelected], asset.assetid) &&
                !isNaN(asset.feesamount)
            ),
            'feesamount'
          ),

          totalspamount: sumBy(
            state.listAssets.filter(
              (asset: any) =>
                _.includes([...newSelected], asset.assetid) &&
                !isNaN(asset.spamount)
            ),
            'spamount'
          ),
          totalvatrefund: sumBy(
            state.listAssets.filter(
              (asset: any) =>
                _.includes([...newSelected], asset.assetid) &&
                !isNaN(asset.vatrefund)
            ),
            'vatrefund'
          ),
          totaltpofamount: sumBy(
            state.listAssets.filter(
              (asset: any) =>
                _.includes([...newSelected], asset.assetid) &&
                !isNaN(asset.tpofamount)
            ),
            'tpofamount'
          ),
          totalinvoiceamount: sumBy(
            state.listAssets.filter(
              (asset: any) =>
                _.includes([...newSelected], asset.assetid) &&
                !isNaN(asset.totalinvoiceamount)
            ),
            'totalinvoiceamount'
          ),
          newOutstanding: newOutstanding,
        });
        updateState({
          listAssets: state.listAssets.map((asset: any) => {
            if (asset.assetid === selectedRow.assetid)
              return {
                ...asset,
                targetphase: '',
                degfeesamount: 0,
                tpofamount: 0,
                saleamount: 0,
                vatrefund: 0,
                spamount: 0,
                totalinvoiceamount: 0,
              };
            return asset;
          }),
          newOutstanding: newOutstanding,
          newRental: newRental,
          newRV: newRVAmount,
          totaldegfeesamount: sumBy(
            state.listAssets.filter(
              (asset: any) =>
                _.includes([...newSelected], asset.assetid) &&
                !isNaN(asset.degfeesamount)
            ),
            'degfeesamount'
          ),
          feesamount: sumBy(
            state.listAssets.filter(
              (asset: any) =>
                _.includes([...newSelected], asset.assetid) &&
                !isNaN(asset.feesamount)
            ),
            'feesamount'
          ),
          totalinvoiceamount: sumBy(
            state.listAssets.filter(
              (asset: any) =>
                _.includes([...newSelected], asset.assetid) &&
                !isNaN(asset.totalinvoiceamount)
            ),
            'totalinvoiceamount'
          ),
          totalspamount: sumBy(
            state.listAssets.filter(
              (asset: any) =>
                _.includes([...newSelected], asset.assetid) &&
                !isNaN(asset.spamount)
            ),
            'spamount'
          ),
          totaltpofamount: sumBy(
            state.listAssets.filter(
              (asset: any) =>
                _.includes([...newSelected], asset.assetid) &&
                !isNaN(asset.tpofamount)
            ),
            'tpofamount'
          ),
          totalvatrefund: sumBy(
            state.listAssets.filter(
              (asset: any) =>
                _.includes([...newSelected], asset.assetid) &&
                !isNaN(asset.vatrefund)
            ),
            'vatrefund'
          ),

          totalsaleamount: sumBy(
            state.listAssets.filter(
              (asset: any) =>
                _.includes([...newSelected], asset.assetid) &&
                !isNaN(asset.saleamount)
            ),
            'saleamount'
          ),
        });
        setselectedassets([...newSelected]);
      }
    },
    hideSelectAll: true,
  };

  const debounceSpvalueChange = debounce(() => {
    form?.setFieldsValue({
      spamount: 0,
      spformula: null,
    });
    updateState({ totalspamount: 0 });
  }, 1000);

  const calculateTotalInvoiceAmount = () => {
    // //
    const totalinvoiceamount =
      state.feesamount +
      state.totalsaleamount +
      state.totalspamount +
      state.totaltpofamount;
    updateState({
      totalinvoiceamount: totalinvoiceamount,
    });
    form?.setFieldsValue({
      totalinvoiceamount: totalinvoiceamount,
    });
  };

  return (
    <DF.Provider pagcode="tabCTEarlyTerm" entity={object} entityid={objectid}>
      <Spin spinning={contactLoading || Loading}>
        <Form form={form} layout="vertical">
          <Row>
            <Row gutter={0}>
              <Col span={15}>
                <Row gutter={0}>
                  <UtinaCard comcode="effectDate" lg={23} level={4}>
                    <Row gutter={10}>
                      <FormInput
                        col
                        xs={7}
                        name="mode"
                        rules={[{ required: true }]}
                        labelAlign="left"
                        initialValue={mode}
                      >
                        <Radio.Group
                          options={optionsMode}
                          onChange={(e) => {
                            setMode(e.target.value);
                            if (e.target.value === 'SIMUL') {
                              setMode('SIMUL');
                              resetToManual();
                              form?.setFieldValue('effectDate', '');
                            }
                          }}
                          value={mode}
                          optionType="button"
                        />
                      </FormInput>

                      <Form.Item
                        shouldUpdate={shouldUpdateCompare('mode')}
                        noStyle
                      >
                        {({ getFieldValue }) => (
                          <FormInput
                            col
                            xs={11}
                            name="modid"
                            labelAlign="left"
                            rules={[{ required: mode === 'REAL' }]}
                          >
                            <Select
                              allowClear
                              style={{ width: '100%' }}
                              onChange={(e: number) => handleFinModSelect(e)}
                              onClear={() => {
                                setMode('SIMUL');
                                resetToManual();
                              }}
                              disabled={mode === 'SIMUL'}
                            >
                              {optionModListValid}
                            </Select>
                          </FormInput>
                        )}
                      </Form.Item>
                      <FormInput
                        labelAlign="left"
                        col
                        xs={6}
                        name="effectDate"
                        rules={[{ required: mode !== 'REAL' }]}
                      >
                        <Select
                          defaultValue=""
                          style={{ width: '100%' }}
                          onChange={handleEffectChange}
                          disabled={
                            state.isModSelected ||
                            state.listAssets?.length === 0
                          }
                        >
                          {optionDates}
                        </Select>
                      </FormInput>
                      <FormInput
                        col
                        xs={7}
                        name="flagapplytoall"
                        // rules={[{ required: true }]}
                        labelAlign="left"
                      >
                        <Radio.Group
                          options={optionsApplyToAll}
                          value={flagapplytoall}
                          optionType="button"
                          disabled={
                            state.isModSelected ||
                            state.listAssets?.length === 1 ||
                            mode === 'REAL'
                          }
                          onChange={(val) =>
                            handleChangeApplyToAll(val.target.value)
                          }
                        />
                      </FormInput>

                      <FormInput
                        col
                        xs={6}
                        name="targetphase"
                        labelAlign="left"
                        rules={[
                          { required: flagapplytoall && mode !== 'REAL' },
                        ]}
                      >
                        <ParListSelect
                          parcode="ASSETEND"
                          onChange={handleTargetPhaseChange}
                          disabled={!flagapplytoall || state.isModSelected}
                          offerid={contract?.offerid}
                          prcode={contract?.prcode}
                        />
                      </FormInput>
                      <Form.Item
                        shouldUpdate={shouldUpdateCompare('targetphase')}
                        noStyle
                      >
                        {({ getFieldValue }) => (
                          <FormInput
                            col
                            xs={11}
                            name="targetstatus"
                            labelAlign="left"
                            rules={[
                              { required: flagapplytoall && mode !== 'REAL' },
                            ]}
                          >
                            <ParListSelect
                              parcode="ASSETSTAT"
                              parvalueparent={getFieldValue('targetphase')}
                              onChange={handletargetstatus}
                              disabled={
                                !getFieldValue('flagapplytoall') ||
                                state.isModSelected ||
                                isNil(getFieldValue('targetphase'))
                              }
                            ></ParListSelect>
                          </FormInput>
                        )}
                      </Form.Item>
                    </Row>
                  </UtinaCard>

                  {flagapplytoall && (
                    <UtinaCard comcode="feesFormula" lg={23} level={4}>
                      <Row gutter={20}>
                        <Form.Item
                          shouldUpdate={shouldUpdateCompare('targetphase')}
                          noStyle
                        >
                          {({ getFieldValue }) => {
                            const display =
                              getFieldValue('targetphase') === 'STOCKIN';
                            if (display) {
                              return (
                                <>
                                  <FormInput
                                    col
                                    xs={8}
                                    labelAlign="left"
                                    name="damage"
                                  >
                                    <ParListSelect
                                      parcode="DAMLEVEL"
                                      onChange={(dmg) => {
                                        form?.setFieldsValue({
                                          totaldegfeesamount: 0,
                                          degfeesformula: null,
                                        });
                                        updateState({
                                          listAssets: state.listAssets.map(
                                            (asset: any) => {
                                              return {
                                                ...asset,
                                                damage: dmg,
                                              };
                                            }
                                          ),
                                          totaldegfeesamount: 0,
                                        });
                                      }}
                                      disabled={state.isModSelected}
                                    />
                                  </FormInput>

                                  <FormInput
                                    labelAlign="left"
                                    col
                                    xs={9}
                                    name="degfeesformula"
                                  >
                                    <PricingListFilteredSelect
                                      //disabled={!getFieldValue('offerid')}
                                      allowClear
                                      disabled={state.isModSelected}
                                      params={{
                                        offerid: contract?.offerid ?? 0,
                                        ctetype: 'DEGFEES', // Modifictaion fees
                                        prcode: contract?.prcode! ?? '',
                                        entity: '',
                                        entityid: 0,
                                        tpid: contract?.tpidclient ?? 0,
                                        filtersPricing:
                                          'DAMLEVEL=' +
                                          getFieldValue('damage') +
                                          ',',
                                      }}
                                      onChange={(e) =>
                                        calculateFees(e, 'DEGFEES')
                                      }
                                    />
                                  </FormInput>
                                  <FormInput
                                    labelAlign="left"
                                    col
                                    xs={7}
                                    name="degfeesamount"
                                  >
                                    <Text>
                                      {toCurrency(
                                        state.totaldegfeesamount,
                                        contract?.currcode
                                      )}
                                    </Text>
                                  </FormInput>

                                  <FormInput
                                    col
                                    xs={8}
                                    name="contractualmillage"
                                    labelAlign="left"
                                    //rules={[{ required: true }]}
                                  >
                                    <InputNumber
                                      onChange={(e) => {
                                        console.log('eee', e);
                                      }}
                                      disabled={state.isModSelected}
                                    />
                                  </FormInput>

                                  <FormInput
                                    col
                                    xs={8}
                                    name="currentMileage"
                                    labelAlign="left"
                                    //rules={[{ required: true }]}
                                  >
                                    <InputNumber
                                      onChange={(e) => {
                                        let diff =
                                          Number(e) -
                                          form?.getFieldValue(
                                            'contractualmillage'
                                          );
                                        form?.setFieldValue(
                                          'spvalue',
                                          diff > 0 ? diff?.toFixed(2) : 0
                                        );
                                      }}
                                      disabled={state.isModSelected}
                                    />
                                  </FormInput>

                                  <FormInput
                                    col
                                    xs={8}
                                    name="spvalue"
                                    labelAlign="left"
                                    //rules={[{ required: true }]}
                                  >
                                    <InputNumber
                                      onChange={(e) => {
                                        debounceSpvalueChange();
                                      }}
                                      disabled={state.isModSelected}
                                    />
                                  </FormInput>

                                  <FormInput
                                    col
                                    xs={9}
                                    labelAlign="left"
                                    name="spformula"
                                    //rules={[{ required: true }]}
                                  >
                                    <PricingListFilteredSelect
                                      //disabled={!getFieldValue('offerid')}
                                      allowClear
                                      disabled={state.isModSelected}
                                      params={{
                                        offerid: contract?.offerid ?? 0,
                                        ctetype: 'SPFEES',
                                        prcode: contract?.prcode! ?? '',
                                        entity: '',
                                        entityid: 0,
                                        tpid: contract?.tpidclient ?? 0,
                                        filtersValue:
                                          'NBKMS=' +
                                          getFieldValue('spvalue') +
                                          ',',
                                      }}
                                      onChange={(e) =>
                                        calculateFees(e, 'SPFEES')
                                      }
                                    />
                                  </FormInput>
                                  <FormInput
                                    col
                                    xs={7}
                                    labelAlign="left"
                                    name="spamount"
                                    //rules={[{ required: true }]}
                                  >
                                    <Text>
                                      {toCurrency(
                                        state.totalspamount,
                                        contract?.currcode
                                      )}
                                    </Text>
                                  </FormInput>
                                </>
                              );
                            } else {
                              return (
                                <>
                                  <FormInput
                                    col
                                    xs={8}
                                    name="totalfinrvamount"
                                    labelAlign="left"
                                  >
                                    <Text>
                                      {toCurrency(
                                        state.totalfinrvamount,
                                        contract?.currcode
                                      )}
                                    </Text>
                                  </FormInput>

                                  <FormInput
                                    col
                                    xs={8}
                                    name="totalirvamount"
                                    labelAlign="left"
                                  >
                                    <Text>
                                      {toCurrency(
                                        state.totalirvamount,
                                        contract?.currcode
                                      )}
                                    </Text>
                                  </FormInput>

                                  <FormInput col xs={8} name="totalsaleamount">
                                    <Text>
                                      {toCurrency(
                                        state.totalsaleamount,
                                        contract?.currcode
                                      )}
                                    </Text>
                                  </FormInput>
                                </>
                              );
                            }
                          }}
                        </Form.Item>

                        <FormInput col xs={17} name="tpofformula">
                          <PricingListFilteredSelect
                            //disabled={!getFieldValue('offerid')}
                            allowClear
                            disabled={state.isModSelected}
                            params={{
                              offerid: contract?.offerid ?? 0,
                              ctetype: 'TPOF', // Total Pay off fees
                              prcode: contract?.prcode! ?? '',
                              entity: '',
                              entityid: 0,
                              tpid: contract?.tpidclient ?? 0,
                              /*filters:
                                      'effectDate=' +
                                      form?.getFieldValue('effectDate') +
                                      ',',*/
                            }}
                            onClear={() => {
                              // //
                              let listAssets = state.listAssets.map(
                                (asset: any) => {
                                  return {
                                    ...asset,
                                    tpofamount: 0,
                                  };
                                }
                              );
                              setState({
                                ...state,
                                totaltpofamount: 0,
                                listAssets: listAssets,
                              });
                              state.totaltpofamount = 0;

                              calculateTotalInvoiceAmount();
                            }}
                            onChange={(e) => calculateFees(e, 'TPOF')}
                          />
                        </FormInput>
                        <FormInput col xs={7} name="tpofamount">
                          <Text>
                            {toCurrency(
                              state.totaltpofamount,
                              contract?.currcode
                            )}
                          </Text>
                        </FormInput>
                        {flagapplytoall && (
                          <FormInput col xs={5} name="fees">
                            <ParListSelectWithLabelInValue
                              parcode="FEESTYPE"
                              parvalueparent="MISCFEES"
                              disabled={state.isModSelected}
                              onSelect={(e) => {
                                form?.setFieldsValue({ feeslabel: e.label });
                              }}
                              onClear={() => {
                                form?.setFieldValue('feesamount', 0);
                              }}
                            />
                          </FormInput>
                        )}
                        <FormInput col xs={10} name="feeslabel">
                          <Input
                            disabled={state.isModSelected}
                            hidden={!flagapplytoall}
                          />
                        </FormInput>

                        <Form.Item
                          shouldUpdate={shouldUpdateCompare('fees')}
                          noStyle
                        >
                          {({ getFieldValue }) => {
                            return (
                              <FormInput
                                col
                                xs={9}
                                name="feesamount"
                                shouldUpdate
                              >
                                <InputCurrency
                                  currcode={
                                    contract?.currcode ??
                                    localStorage.getItem('currcode') ??
                                    ''
                                  }
                                  hidden={!flagapplytoall}
                                  onChange={(e) => {
                                    state.feesamount = e;
                                    setState({
                                      ...state,
                                      feesamount: e,
                                    });
                                    calculateTotalInvoiceAmount();
                                  }}
                                  disabled={
                                    state.isModSelected ||
                                    getFieldValue('fees') === undefined ||
                                    getFieldValue('fees') === ''
                                  }
                                />
                              </FormInput>
                            );
                          }}
                        </Form.Item>
                      </Row>
                    </UtinaCard>
                  )}
                </Row>
              </Col>

              <Col span={9}>
                <UtinaCard
                  comcode="effectDateSituation"
                  lg={23}
                  level={4}
                  noMarginTop
                >
                  <Row>
                    <FormInput
                      labelAlign="left"
                      col
                      xs={12}
                      name="currentRental"
                      noMargin
                      colStyle={{ margin: 0 }}
                    >
                      <Text>
                        {toCurrency(state.currentRental, contract?.currcode)}
                      </Text>
                    </FormInput>
                    <FormInput
                      labelAlign="left"
                      col
                      xs={11}
                      name="newRental"
                      noMargin
                      colStyle={{ margin: 0 }}
                    >
                      <Text>
                        {toCurrency(state.newRental, contract?.currcode)}
                      </Text>
                    </FormInput>

                    <FormInput
                      col
                      xs={12}
                      name="currentOutstanding"
                      noMargin
                      colStyle={{ margin: 0 }}
                    >
                      <Text>
                        {toCurrency(
                          state.currentOutstanding,
                          contract?.currcode
                        )}
                      </Text>
                    </FormInput>
                    <FormInput
                      col
                      xs={11}
                      name="newOutstanding"
                      noMargin
                      colStyle={{ margin: 0 }}
                    >
                      <Text>
                        {toCurrency(state.newOutstanding, contract?.currcode)}
                      </Text>
                    </FormInput>

                    <FormInput
                      col
                      xs={12}
                      name="currentRV"
                      noMargin
                      labelAlign="left"
                      colStyle={{ margin: 0 }}
                    >
                      <Text>
                        {toCurrency(state.currentRV, contract?.currcode)}
                      </Text>
                    </FormInput>
                    <FormInput
                      col
                      xs={11}
                      name="newRV"
                      noMargin
                      colStyle={{ margin: 0 }}
                    >
                      <Text>{toCurrency(state.newRV, contract?.currcode)}</Text>
                    </FormInput>
                  </Row>
                </UtinaCard>
                <UtinaCard
                  comcode="sale&feesAmount"
                  lg={23}
                  level={4}
                  noMarginTop
                >
                  <Row>
                    <FormInput col xs={12} name="totalinvoiceamount" noMargin>
                      <Text>
                        {toCurrency(
                          state.totalinvoiceamount,
                          contract?.currcode
                        )}
                      </Text>
                    </FormInput>

                    <FormInput col xs={12} name="totalsaleamount" noMargin>
                      <Text>
                        {toCurrency(state.totalsaleamount, contract?.currcode)}
                      </Text>
                    </FormInput>
                    <FormInput col xs={12} name="totaltpofamount" noMargin>
                      <Text>
                        {toCurrency(state.totaltpofamount, contract?.currcode)}
                      </Text>
                    </FormInput>
                    <FormInput
                      col
                      xs={12}
                      name="totaldegfeesamount"
                      noMargin
                      labelAlign="left"
                    >
                      <Text>
                        {toCurrency(
                          state.totaldegfeesamount,
                          contract?.currcode
                        )}
                      </Text>
                    </FormInput>
                    <FormInput col xs={11} name="totalspamount" noMargin>
                      <Text>
                        {toCurrency(state.totalspamount, contract?.currcode)}
                      </Text>
                    </FormInput>
                    {/* <FormInput col xs={11} name="totalvat" noMargin>
                      <Text>
                        {toCurrency(state.totalvatrefund, contract?.currcode)}
                      </Text>
                    </FormInput> */}
                    <FormInput
                      col
                      xs={11}
                      name="feesamount"
                      noMargin
                      shouldUpdate
                    >
                      <Text>
                        {toCurrency(
                          form?.getFieldValue('feesamount'),
                          contract?.currcode
                        )}
                      </Text>
                    </FormInput>

                    <FormInput
                      col
                      xs={11}
                      name="niemetvaamount"
                      noMargin
                      shouldUpdate
                    >
                      <Text>
                        {toCurrency(
                          form?.getFieldValue('niemetvaamount'),
                          contract?.currcode
                        )}
                      </Text>
                    </FormInput>
                    <FormInput
                      col
                      xs={11}
                      name="cteaprrate"
                      noMargin
                      shouldUpdate
                    >
                      <Text>{toRate(cteaprrate)}</Text>
                    </FormInput>
                  </Row>
                </UtinaCard>
              </Col>
            </Row>

            {/* <Col span={8}>
              <div style={{ background: '#F6F3F2' }}>
                <Row>
                  <FormInput col xs={24} name="totalinvoiceamount" noMargin>
                    <Text>
                      {toCurrency(state.totalinvoiceamount, contract?.currcode)}
                    </Text>
                  </FormInput>
                </Row>
                <Row>
                  <FormInput col xs={12} name="totalsaleamount" noMargin>
                    <Text>
                      {toCurrency(state.totalsaleamount, contract?.currcode)}
                    </Text>
                  </FormInput>
                  <Col span={1} />
                  <FormInput col xs={11} name="totaltpofamount" noMargin>
                    <Text>
                      {toCurrency(state.totaltpofamount, contract?.currcode)}
                    </Text>
                  </FormInput>
                </Row>
                <Row>
                  <FormInput
                    wrapperCol={{ span: 8 }}
                    labelCol={{ span: 16 }}
                    col
                    xs={12}
                    name="totaldegfeesamount"
                    noMargin
                    labelAlign="left"
                  >
                    <Text>
                      {toCurrency(state.totaldegfeesamount, contract?.currcode)}
                    </Text>
                  </FormInput>
                  <Col span={1} />
                  <FormInput
                    wrapperCol={{ span: 12 }}
                    labelCol={{ span: 12 }}
                    col
                    xs={11}
                    name="totalspamount"
                    noMargin
                  >
                    <Text>
                      {toCurrency(state.totalspamount, contract?.currcode)}
                    </Text>
                  </FormInput>
                </Row>
              </div>
              <div style={{ background: '#F6F3F2' }}>
                <Row>
                  <FormInput
                    wrapperCol={{ span: 12 }}
                    labelCol={{ span: 12 }}
                    labelAlign="left"
                    col
                    xs={12}
                    name="currentRental"
                    noMargin
                  >
                    <Text>
                      {toCurrency(state.currentRental, contract?.currcode)}
                    </Text>
                  </FormInput>
                  <Col span={1} />
                  <FormInput
                    wrapperCol={{ span: 12 }}
                    labelCol={{ span: 12 }}
                    labelAlign="left"
                    col
                    xs={11}
                    name="newRental"
                    noMargin
                  >
                    <Text>
                      {toCurrency(state.newRental, contract?.currcode)}
                    </Text>
                  </FormInput>
                </Row>
                <Row>
                  <FormInput
                    wrapperCol={{ span: 12 }}
                    labelCol={{ span: 12 }}
                    col
                    xs={12}
                    name="currentOutstanding"
                    noMargin
                  >
                    <Text>
                      {toCurrency(state.currentOutstanding, contract?.currcode)}
                    </Text>
                  </FormInput>
                  <Col span={1} />
                  <FormInput
                    wrapperCol={{ span: 12 }}
                    labelCol={{ span: 12 }}
                    col
                    xs={11}
                    name="newOutstanding"
                    noMargin
                  >
                    <Text>
                      {toCurrency(state.newOutstanding, contract?.currcode)}
                    </Text>
                  </FormInput>
                </Row>
                <Row>
                  <FormInput
                    wrapperCol={{ span: 12 }}
                    labelCol={{ span: 12 }}
                    col
                    xs={12}
                    name="currentRV"
                    noMargin
                    labelAlign="left"
                  >
                    <Text>
                      {toCurrency(state.currentRV, contract?.currcode)}
                    </Text>
                  </FormInput>
                  <Col span={1} />
                  <FormInput
                    wrapperCol={{ span: 12 }}
                    labelCol={{ span: 12 }}
                    col
                    xs={11}
                    name="newRV"
                    noMargin
                  >
                    <Text>{toCurrency(state.newRV, contract?.currcode)}</Text>
                  </FormInput>
                </Row>
              </div>
            </Col> */}
            <UtinaCard comcode="assets" level={4} lg={23} noMarginTop>
              <AssetsTable
                currcode={contract?.currcode}
                size="small"
                dataSource={state.listAssets}
                scroll={{ x: 1000, y: 400 }}
                pagination={state?.listAssets?.length > 10 && { pageSize: 10 }}
                rowSelection={
                  // selectedassets.length === 0
                  //   ? {
                  //       type: 'checkbox',
                  //       ...rowSelection,
                  //     }
                  // :
                  { type: 'checkbox', ...allrowSelection }
                }
                assets={state.listAssets}
                isLoading={contactLoading}
                ctrid={contract?.ctrid}
                offerid={contract?.offerid}
                prcode={contract?.prcode}
                tpidclient={contract?.tpidclient}
                effectDate={form?.getFieldValue('effectDate')}
                handleTarget={handleTarget}
                disabled={state?.isModSelected}
                hideModalAsset={flagapplytoall && !state?.isModSelected}
                //rowKey={(record) => record.assetid}
                rowKey="assetid"
                vatrefundduration={Number(vatrefundduration)}
              />
            </UtinaCard>
            <UtinaCard comcode="comment" level={4} lg={23}>
              <Row gutter={30}>
                <FormInput col xs={8} name="reason">
                  <ParListSelect
                    parcode="ENDREASON"
                    disabled={state.isModSelected}
                  />
                </FormInput>

                <FormInput
                  col
                  xs={16}
                  name="comment"
                  rules={[{ required: mode !== 'REAL' }]}
                >
                  <TextArea rows={4} />
                </FormInput>
              </Row>
            </UtinaCard>
          </Row>
        </Form>
      </Spin>
    </DF.Provider>
  );
};

export default TabContractEarlyTermination;
